// components/WaitingRoom/SessionTimer.jsx
import React, { useState, useEffect, useRef } from 'react';
import { ClockCircleOutlined, WarningOutlined } from '@ant-design/icons';

/**
 * Contador flotante de sesion.
 * Recibe `serverSeconds` del polling (cada 5s) y hace countdown local cada 1s.
 * Muestra alerta cuando quedan menos de 2 minutos.
 */
const SessionTimer = ({ serverSeconds }) => {
    const [seconds, setSeconds] = useState(serverSeconds);
    const [dismissed, setDismissed] = useState(false);
    const intervalRef = useRef(null);

    // Sincronizar con el valor del servidor en cada polling
    useEffect(() => {
        if (serverSeconds != null) {
            setSeconds(serverSeconds);
        }
    }, [serverSeconds]);

    // Countdown local cada segundo
    useEffect(() => {
        intervalRef.current = setInterval(() => {
            setSeconds(prev => (prev != null && prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => clearInterval(intervalRef.current);
    }, []);

    // No mostrar si no hay timeout, ya expiro, o fue cerrado manualmente
    if (serverSeconds == null || seconds == null || seconds <= 0) return null;
    if (dismissed) return null;

    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    const timeStr = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;

    const isWarning = seconds <= 120; // 2 minutos
    const isCritical = seconds <= 30;

    return (
        <div style={{
            ...styles.container,
            ...(isCritical ? styles.critical : isWarning ? styles.warning : styles.normal),
            ...(isCritical ? { animation: 'pulse 1s ease-in-out infinite' } : {}),
        }}>
            <div style={styles.content}>
                {isWarning
                    ? <WarningOutlined style={styles.icon} />
                    : <ClockCircleOutlined style={styles.icon} />
                }
                <div style={styles.textBlock}>
                    <span style={styles.label}>
                        {isCritical ? 'Sesion por expirar' : isWarning ? 'Tiempo restante' : 'Sesion activa'}
                    </span>
                    <span style={{
                        ...styles.time,
                        ...(isCritical ? { color: '#fff' } : isWarning ? { color: '#92400e' } : {}),
                    }}>
                        {timeStr}
                    </span>
                </div>
                {!isWarning && (
                    <button
                        onClick={() => setDismissed(true)}
                        style={styles.closeBtn}
                        title="Ocultar"
                    >
                        x
                    </button>
                )}
            </div>

            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.7; }
                }
                @keyframes slideIn {
                    from { transform: translateY(-20px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
            `}</style>
        </div>
    );
};

const styles = {
    container: {
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 9999,
        borderRadius: 12,
        padding: '10px 16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        animation: 'slideIn 0.3s ease-out',
        minWidth: 180,
    },
    normal: {
        background: '#f0f9ff',
        border: '1px solid #bae6fd',
    },
    warning: {
        background: '#fffbeb',
        border: '1px solid #fcd34d',
    },
    critical: {
        background: '#dc2626',
        border: '1px solid #b91c1c',
    },
    content: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
    },
    icon: {
        fontSize: 18,
    },
    textBlock: {
        display: 'flex',
        flexDirection: 'column',
        lineHeight: 1.2,
        flex: 1,
    },
    label: {
        fontSize: 11,
        fontWeight: 500,
        opacity: 0.8,
    },
    time: {
        fontSize: 18,
        fontWeight: 700,
        fontFamily: 'monospace',
        letterSpacing: 1,
        color: '#0c4a6e',
    },
    closeBtn: {
        background: 'none',
        border: 'none',
        fontSize: 14,
        cursor: 'pointer',
        opacity: 0.5,
        padding: '0 4px',
        lineHeight: 1,
    },
};

export default SessionTimer;
